/* eslint-disable max-len*/
import { ReactElement, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SingleValue } from 'react-select';
import StyledSingleDropdown from '../../../../components/StyledSingleDropdown/StyledSingleDropdown';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  selectSchoolYear,
  setSchoolYearFilter,
  selectBoardMembersLoadingListItems,
  selectFilteredBoardMembersListItems,
  selectSchoolYears,
  selectLoadingSchoolYears
} from '../../../../redux/slices/boardMembersSlice';
import { TooltipAnchor } from '../../../../services/app.service';
import { FormDropdownSelectOption } from '../../../../types/propTypes/FormDropdownPropTypes';
import { ReactSelectOption } from '../../../../types/ReactSelectOption';
import { BoardMembersListItemModel } from '../../../../types/BoardMembersListItemModel';
import { isAdmin } from '../../../../utilities/userUtilities';
import './BoardMembersList.css';
import DistrictSchoolInfoPage from '../DistrictSchoolInfoPage';
import { fetchBoardMembersListItems, fetchBoardMembersSchoolYears } from 'src/redux/thunks/boardMembersThunks';
import { abortPromiseOnUnmount } from '../../../../services/base.service';
import { appRoutePaths } from '../../../../services/route.service';
import { useConfirmationModalContext } from '../../../../hooks/useConfirmationModalContext';
import { isValidCode } from '../../../../services/base.service';
import { ApiResponseModel } from '../../../../types/ApiResponseModel';
import { deleteBoardMember } from '../../../../services/boardMembers.service';
import { formatToDateString } from '../../../../utilities/dateUtilities';

const BoardMembersList = (): ReactElement => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const schoolYear = useAppSelector(selectSchoolYear);
  const isLoading = useAppSelector(selectBoardMembersLoadingListItems);
  const listItems: BoardMembersListItemModel[] = useAppSelector(
    selectFilteredBoardMembersListItems 
  );
  const confirmContext = useConfirmationModalContext();
  const schoolYears = useAppSelector(selectSchoolYears);
  const isMostRecentYearSelected = (): boolean => schoolYears?.length > 0 && schoolYear.value === schoolYears[0]?.value;
  const loadingSchoolYears = useAppSelector(selectLoadingSchoolYears);

  const getSelectedYearFromOptions = (value: string | undefined) : ReactSelectOption | undefined => {
    const selectOption = 
      schoolYears.find((option) => option.value === value);
    return { value: selectOption?.value ?? '', label: selectOption?.text ?? '' };
  }

   const selectedOption = getSelectedYearFromOptions(schoolYear.value);

  const handleEdit = (id: string): void => {
    const allowEdit = isMostRecentYearSelected();
    navigate(
      appRoutePaths.BoardMembersInput(id),
      { state: { allowEdit: allowEdit } }
    );
  };

  const handleNew = (): void => {
    navigate(
      appRoutePaths.BoardMembersInput('new'),
      { state: { allowEdit: true } }
    );
  };

  const formatDateForDisplay = (dateString: string | null | undefined): string => {
    if (!dateString) {
      return '';
    }

    const formattedDate = formatToDateString(dateString);

    if (formattedDate) {
      return formattedDate;
    } else {
      return dateString;
    }
  };

  useEffect(() => {
    const promise: unknown = dispatch(fetchBoardMembersListItems());
    return () => {
      abortPromiseOnUnmount(promise);
    };
  }, [dispatch]);

  useEffect(() => {
    const promise: unknown = dispatch(fetchBoardMembersSchoolYears());
    return () => {
      abortPromiseOnUnmount(promise);
    };
  }, [dispatch]);
 
  const handleOnChange = async (
    option: FormDropdownSelectOption
  ): Promise<void> => {
    const selectedValue = (option as SingleValue<ReactSelectOption>)?.value;
    const selectedSchoolYear = getSelectedYearFromOptions(selectedValue);
    selectedValue && dispatch(setSchoolYearFilter({value: selectedSchoolYear?.value ?? '', text: selectedSchoolYear?.label ?? ''}));
  };

  const handleDelete = async (
    id: string,
    lea: string
  ): Promise<void> => {
    confirmContext.setOptions({
      title: `Delete Board Member?`,
      content: (
        <div className="confirm--message">
          Are you sure you want to delete this board member?
        </div>
      ),
      confirmText: 'Yes',
      cancelText: 'No',
      onOk: async (): Promise<ApiResponseModel<unknown>> => {
        const response = await deleteBoardMember(id, lea);
        if (isValidCode(response.status)) {
          /* istanbul ignore next */
          dispatch(fetchBoardMembersListItems());
        }
        return response;
      },
    });

    await confirmContext.showConfirmation();
  };

  return (
    <DistrictSchoolInfoPage
      pageClass="data-input--student--student-intervention--list"
      isLoading={isLoading}
      loadingDataId={'board-members-list-loader'}
    >
      <div className="content-outer-container">
        <div className="content-inner-container">
          <div
            className={
              isAdmin()
                ? 'school-year-selection-container'
                : 'school-year-selection-container school-year-padding'
            }
          >
            { !loadingSchoolYears && (
            <StyledSingleDropdown
              fieldName="schoolYear"
              options={ schoolYears }
              onChange={handleOnChange}
              value={selectedOption}
              isSearchable={false}
            />
            )}
          </div>
          <div className="subheading-and-button-container">
            <h4 data-testid={'component-heading-text'}>Board Members</h4>
                <button
                  data-testid={`add-button`}
                  aria-label="Edit"
                  onClick={() => handleNew()}
                  data-tooltip-id={TooltipAnchor}
                  data-tooltip-content="Add New Board Member"
                  className='button no-wrap-text new-button'
                  style={{ visibility: isMostRecentYearSelected() && isAdmin() ? 'visible' : 'hidden' }}
                >
                  Add New
                </button>
          </div>
          <div className="table-container">
            <table cellSpacing={0} className="page-view-table">
              <thead>
                <tr>
                  <th>Last Name</th>
                  <th>First Name</th>
                  <th>Email Address</th>
                  <th>Position Type</th>
                  <th>Term Start</th>
                  <th>Term End</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {listItems?.length > 0 &&
                  listItems.map((boardMember) => (
                    <tr
                      key={`${boardMember.id}`}
                      data-testid={boardMember.id}
                    >
                      <td>{boardMember.lastOrSurname}</td>
                      <td>{boardMember.firstName}</td>
                      <td>{boardMember.email}</td>
                      <td>{boardMember.positionType}</td>
                      <td>{formatDateForDisplay(boardMember.termStartDate)}</td>
                      <td>{formatDateForDisplay(boardMember.termEndDate)}</td>
                      <td>
                        <div className="action--column">
                          <button
                            data-testid={`edit-button-${boardMember.id}`}
                            aria-label="Edit"
                            onClick={() => handleEdit(boardMember.id)}
                            data-tooltip-id={TooltipAnchor}
                            data-tooltip-content={isMostRecentYearSelected() 
                              ? 'View/Edit Board Member' 
                              : 'View Board Member'}
                          >
                            <img src={'/images/icon-edit.svg'} />
                          </button>
                          {isAdmin() && isMostRecentYearSelected() &&
                              <button
                                onClick={async () => {
                                  await handleDelete(boardMember.id, boardMember.leaIdentifier);
                                }}
                                data-test-id={`delete-button-${boardMember.id}`}
                                data-tooltip-id={TooltipAnchor}
                                data-tooltip-content={'Delete Board Member'}
                              >
                                <img src={'/images/icon-trash.svg'} />
                              </button>
                          }
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DistrictSchoolInfoPage>
  );
};

export default BoardMembersList;
